import React, { useEffect, useState } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import {
  Building2, Users as UsersIcon, Layers, Power, ShieldCheck,
  Activity, Loader2, ChevronDown, ChevronRight, UserCircle2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Stat = ({ label, value, icon: Icon, color, testid }) => (
  <div className={`rounded-xl border p-5 ${color || 'bg-white border-slate-200'}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      {Icon && <Icon className="w-4 h-4 text-slate-400" />}
    </div>
    <p className="text-3xl font-semibold text-slate-900 mt-2 font-mono">{value ?? '—'}</p>
  </div>
);

const TenantCard = ({ tenant }) => {
  const [open, setOpen] = useState(false);
  const companies = tenant.companies || [];
  const hasCompanies = companies.length > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`tenant-card-${tenant.code || tenant.id}`}>
      <button
        onClick={() => hasCompanies && setOpen(!open)}
        data-testid={`tenant-toggle-${tenant.code || tenant.id}`}
        className={`w-full flex items-center justify-between px-5 py-4 text-left transition-colors ${hasCompanies ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {hasCompanies ? (
            open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
          ) : (
            <span className="w-4 shrink-0" />
          )}
          <div className="w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0">
            <Building2 className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-slate-900 truncate">{tenant.name}</p>
              {tenant.code && <span className="text-[11px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{tenant.code}</span>}
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${tenant.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                {tenant.status || 'active'}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {tenant.company_count} {tenant.company_count === 1 ? 'company' : 'companies'} · {tenant.user_count} users · {tenant.employee_count} employees
            </p>
          </div>
        </div>
        <div className="flex items-center gap-5 shrink-0 text-right">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Companies</p>
            <p className="text-lg font-mono font-semibold text-slate-900">{tenant.company_count}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Users</p>
            <p className="text-lg font-mono font-semibold text-slate-900">{tenant.user_count}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Employees</p>
            <p className="text-lg font-mono font-semibold text-indigo-700">{tenant.employee_count}</p>
          </div>
        </div>
      </button>

      {open && hasCompanies && (
        <div className="border-t border-slate-200 bg-slate-50/60" data-testid={`tenant-companies-${tenant.code || tenant.id}`}>
          <table className="w-full text-sm">
            <thead className="text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="text-left px-5 py-2.5 font-medium text-xs uppercase tracking-wider">Company</th>
                <th className="text-left px-3 py-2.5 font-medium text-xs uppercase tracking-wider">Code</th>
                <th className="text-left px-3 py-2.5 font-medium text-xs uppercase tracking-wider">Status</th>
                <th className="text-right px-3 py-2.5 font-medium text-xs uppercase tracking-wider">Users</th>
                <th className="text-right px-5 py-2.5 font-medium text-xs uppercase tracking-wider">Employees</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {companies.map((c) => (
                <tr key={c.id} data-testid={`company-row-${c.code || c.id}`} className="hover:bg-white">
                  <td className="px-5 py-3 font-medium text-slate-900 flex items-center gap-2">
                    <Layers className="w-3.5 h-3.5 text-slate-400" />
                    {c.name}
                  </td>
                  <td className="px-3 py-3 text-slate-600 font-mono text-xs">{c.code || '—'}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-slate-700">{c.user_count}</td>
                  <td className="px-5 py-3 text-right font-mono font-semibold text-indigo-700">{c.employee_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const PlatformDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  useEffect(() => { fetchSummary(); }, []);

  const fetchSummary = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/platform/summary`, { withCredentials: true });
      setSummary(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <MainLayout><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div></MainLayout>;
  }

  const s = summary || {};

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="platform-dashboard">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            <ShieldCheck className="inline-block w-8 h-8 mr-2 mb-1 text-[#0052FF]" />
            Platform Console
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            High-level overview across all tenants. Click any tenant to drill down into its companies and headcount.
          </p>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat testid="kpi-total-tenants" label="Total Tenants" value={s.total_tenants} icon={Building2} color="bg-blue-50 border-blue-200" />
          <Stat testid="kpi-active-tenants" label="Active Tenants" value={s.active_tenants} icon={Power} color="bg-green-50 border-green-200" />
          <Stat testid="kpi-total-companies" label="Total Companies" value={s.total_companies} icon={Layers} color="bg-purple-50 border-purple-200" />
          <Stat testid="kpi-total-users" label="Total Users" value={s.total_users} icon={UsersIcon} color="bg-amber-50 border-amber-200" />
          <Stat testid="kpi-total-employees" label="Total Employees" value={s.total_employees} icon={UserCircle2} color="bg-indigo-50 border-indigo-200" />
        </div>

        {/* Tenants drill-down */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-slate-500" />
            <h2 className="text-base font-semibold text-slate-900">Tenant Breakdown</h2>
            <span className="text-xs text-slate-500">({(s.tenants || []).length})</span>
          </div>

          {(s.tenants || []).length === 0 && (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl px-5 py-10 text-center text-slate-400" data-testid="no-tenants">
              No tenants yet. Create one from the Tenants page.
            </div>
          )}

          {(s.tenants || []).map((t) => (
            <TenantCard key={t.id} tenant={t} />
          ))}
        </div>
      </div>
    </MainLayout>
  );
};

export default PlatformDashboard;
